import { z } from 'zod';

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export const ReportingPeriodDto = z.object({
  periodId: z.string().uuid(),
  name: z.string(),
  startDate: z.string().regex(DATE_REGEX),
  endDate: z.string().regex(DATE_REGEX),
  status: z.enum(['open', 'closed']),
  createdAt: z.string(),
  unresolvedCount: z.number().int(),
});
export type ReportingPeriodDto = z.infer<typeof ReportingPeriodDto>;

export const AdminReportingPeriodsResponse = z.object({
  periods: z.array(ReportingPeriodDto),
});
export type AdminReportingPeriodsResponse = z.infer<typeof AdminReportingPeriodsResponse>;

export const CreateReportingPeriodRequest = z
  .object({
    name: z.string().min(1).max(100),
    startDate: z.string().regex(DATE_REGEX),
    endDate: z.string().regex(DATE_REGEX),
  })
  .refine((d) => d.endDate >= d.startDate, {
    message: 'endDate must be on or after startDate',
    path: ['endDate'],
  });
export type CreateReportingPeriodRequest = z.infer<typeof CreateReportingPeriodRequest>;

/** Longest reason an administrator can record against a close or reopen. */
export const REPORTING_PERIOD_REASON_MAX = 1000;

/**
 * Close or reopen a period. The reason is written to the audit event's `comment`.
 *
 * Asymmetric on purpose. Closing is the routine end of a month, so a reason is
 * welcome but optional. Reopening un-finalises a month that has already been
 * reported — edits can then land against data someone has relied on — so it is the
 * transition the audit rule exists for, and F-02 and the logging-fields table both
 * name reopens as requiring a reason. The rule lives in this schema, which the API
 * validates against, so a non-UI caller cannot reopen without one either.
 */
export const PatchReportingPeriodRequest = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('closed'),
    reason: z.string().trim().max(REPORTING_PERIOD_REASON_MAX).optional(),
  }),
  z.object({
    status: z.literal('open'),
    reason: z
      .string()
      .trim()
      .min(1, 'A reason is required to reopen a period')
      .max(REPORTING_PERIOD_REASON_MAX),
  }),
]);
export type PatchReportingPeriodRequest = z.infer<typeof PatchReportingPeriodRequest>;

export const PeriodUnresolvedUserDto = z.object({
  userId: z.string().uuid(),
  displayName: z.string(),
  initials: z.string(),
  utEid: z.string().nullable(),
  teamName: z.string().nullable(),
  weekCount: z.number().int(),
  pendingCount: z.number().int(),
  missingCount: z.number().int(),
});
export type PeriodUnresolvedUserDto = z.infer<typeof PeriodUnresolvedUserDto>;

export const PeriodUnresolvedResponse = z.object({
  people: z.number().int(),
  pending: z.number().int(),
  missing: z.number().int(),
  timesheets: z.number().int(),
  total: z.number().int(),
  users: z.array(PeriodUnresolvedUserDto),
});
export type PeriodUnresolvedResponse = z.infer<typeof PeriodUnresolvedResponse>;

// ---- Submission deadline ----------------------------------------------------
//
// When a week's hours are due. Drives the past-due filter, the urgency rails and
// the "oldest is N days past due" line in the approvals triage view — so it
// publicly labels named colleagues as late, and I-01 requires an administrator to
// be able to change a validation rule like this without a code change.
//
// Stored as one row in system_settings rather than per period: the rule is a
// department-wide policy, and most weeks are covered by no reporting_periods row
// at all, so a per-period column would need a fallback for the common case.

/** 1 = Monday … 7 = Sunday, in the week *following* the week being judged. */
export const SubmissionDeadlineDto = z.object({
  dayOfWeek: z.number().int().min(1).max(7),
  hour: z.number().int().min(0).max(23),
  minute: z.number().int().min(0).max(59),
});
export type SubmissionDeadlineDto = z.infer<typeof SubmissionDeadlineDto>;

export const SubmissionDeadlineResponse = z.object({ deadline: SubmissionDeadlineDto });
export type SubmissionDeadlineResponse = z.infer<typeof SubmissionDeadlineResponse>;

export const PutSubmissionDeadlineRequest = SubmissionDeadlineDto;
export type PutSubmissionDeadlineRequest = z.infer<typeof PutSubmissionDeadlineRequest>;

/**
 * What the rule reads as in the UI, for the triage footnote and the admin pane.
 * Kept next to the schema so the sentence and the value cannot drift apart.
 */
export const WEEKDAY_NAMES = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

export function formatSubmissionDeadline(d: SubmissionDeadlineDto): string {
  const day = WEEKDAY_NAMES[d.dayOfWeek - 1] ?? 'Monday';
  const suffix = d.hour < 12 ? 'AM' : 'PM';
  const h12 = d.hour % 12 === 0 ? 12 : d.hour % 12;
  const mm = String(d.minute).padStart(2, '0');
  return `${day} ${h12}:${mm} ${suffix}`;
}
